import { Injectable } from '@angular/core';
import { SetOptions } from 'firebase/firestore';
import { Guid } from 'guid-typescript';
import { toValidDate } from '@shared/helpers/convert-to-date';
import { toFirestorePlainObject } from '@shared/helpers/firestore-deep-clone';
import { DeviceCounter } from '@shared/models/device-info';
import { Metadata } from '@shared/models/metadata';
import { BaseReplicableService } from './base-replicable.service';
import {
  counterDocumentId,
  DEVICE_COUNTERS_COLLECTION,
  DeviceCounterEntity,
  DeviceCounterEntityType,
  DeviceCounterOperation,
  DeviceCounterOperationResult,
  DeviceCounterRevision,
  DeviceCounterStateChange,
  DeviceCounterStore,
  DeviceCounterSyncOutcome
} from './device-counter/device-counter.models';
import { applyIncrement, applyReset, applySet, toWireCounter } from './device-counter/device-counter-transitions';

/** The Firestore document: the counter plus the concurrency/provenance block. */
export type DeviceCounterDocument = DeviceCounterEntity & { revision?: DeviceCounterRevision };

/** Desktop (PouchDB) transport fields that never belong in the document. */
const DESKTOP_TRANSPORT_FIELDS: readonly string[] = ['_id', '_rev'];

/** Blocks this service maintains itself; never taken from a caller-supplied state. */
const MANAGED_FIELDS: readonly string[] = ['metadata', 'revision'];

/** The counter fields every write carries (a counter is written whole — it is tiny and the value is absolute). */
const COUNTER_FIELDS: readonly (keyof DeviceCounterEntity)[] = ['cashRegisterId', 'entityType', 'current', 'lastResetAt', 'createdAt'];

const FIRESTORE_SOURCE = 'Firestore';

/** Produces the change to write given the locally known document; must be pure. */
type ChangePlanner = (current: DeviceCounterDocument | undefined, documentId: string) => DeviceCounterStateChange;

type CounterTransition = (current: DeviceCounterEntity | undefined, cashRegisterId: string, now: Date) => DeviceCounterEntity;

/**
 * Firestore store for the POS counters — `deviceCounters/{tenantId}_{cashRegisterId}_{entityType}`
 * holds the latest value of each of a cash register's counters, and is the
 * source of truth. The numbers it mints are the register's (`SO-{code}-{n}`,
 * `Closure-{code}-{n}`…), so the document follows the register, not the device
 * it happens to be paired with.
 *
 * Write discipline (the same as the cash register store): every mutation is
 * serialised per document and under a Web Lock, so two tabs cannot mint the
 * same code; the stored document is read from the local cache and then from the
 * server, so a cold cache cannot restart a series at 1 over a real value; and
 * writes are durable queued `setDoc(mergeFields)`s carrying absolute values, so
 * replaying one is harmless.
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceCounterReplicationService extends BaseReplicableService<DeviceCounterEntity> implements DeviceCounterStore {
  protected readonly entityType = 'deviceCounter';
  protected readonly endpoint = 'DeviceCounters';
  protected readonly firestoreCollection = DEVICE_COUNTERS_COLLECTION;

  /** Per-document write chains — see {@link serialize}. */
  private readonly writeChains = new Map<string, Promise<void>>();

  //#region DeviceCounterStore

  /** The register's stored counter, or `undefined` when it does not exist yet — the contract consumers branch on. */
  async getAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounter | undefined> {
    if (!cashRegisterId) return undefined;
    const document = await this.readCurrentAsync(cashRegisterId, entityType);
    return document ? toWireCounter(document) : undefined;
  }

  async incrementAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult> {
    return this.mutateAsync(cashRegisterId, entityType, 'increment', (current, id, now) => applyIncrement(current, id, entityType, now));
  }

  async resetAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterOperationResult> {
    return this.mutateAsync(cashRegisterId, entityType, 'reset', (current, id, now) => applyReset(current, id, entityType, now));
  }

  async setAsync(cashRegisterId: string, entityType: DeviceCounterEntityType, value: number): Promise<DeviceCounterOperationResult> {
    return this.mutateAsync(cashRegisterId, entityType, 'set', (current, id, now) => applySet(current, id, entityType, value, now));
  }

  /**
   * Perform a counter mutation. Answers a result whose `counter` is
   * `undefined` rather than throwing when no register was given, the contract
   * consumers already branch on.
   *
   * There is deliberately no stale guard: the new value is computed from the
   * document this client just read, so it is by definition the newest state —
   * refusing it would return a value that was never persisted.
   */
  private async mutateAsync(
    cashRegisterId: string,
    entityType: DeviceCounterEntityType,
    operation: DeviceCounterOperation,
    transition: CounterTransition
  ): Promise<DeviceCounterOperationResult> {
    if (!cashRegisterId) {
      return { counter: undefined, firestoreSync: Promise.resolve({ status: 'skipped', reason: 'primaryFailed' }) };
    }

    const { state, outcome, change } = await this.applyAsync(cashRegisterId, entityType, (current, documentId) => {
      const now = new Date();
      return {
        cashRegisterId,
        documentId,
        entityType,
        operation,
        occurredAt: now,
        state: transition(current ? this.normalizeDates(current) : undefined, cashRegisterId, now),
        correlationId: Guid.create().toString(),
        source: FIRESTORE_SOURCE,
        primaryOperation: `set ${this.firestoreCollection}/${documentId}`
      };
    });
    return { counter: toWireCounter(state), firestoreSync: Promise.resolve(outcome), change };
  }

  //#endregion

  //#region BaseReplicableService integration

  /** One document per tenant, register and counter. */
  protected override getDocumentId(entity: DeviceCounterEntity): string {
    return this.documentIdOf(entity.cashRegisterId, entity.entityType);
  }

  /** Counters are never pushed to the backend, so `syncInfo` does not apply. */
  protected override ensureValidMetadata(entity: DeviceCounterEntity): void {
    super.ensureValidMetadata(entity);
    delete entity.metadata!.syncInfo;
  }

  //#endregion

  //#region Compare-then-queue

  private documentIdOf(cashRegisterId: string, entityType: DeviceCounterEntityType): string {
    return counterDocumentId(this.authService.currentTenant, cashRegisterId, entityType);
  }

  private async applyAsync(
    cashRegisterId: string,
    entityType: DeviceCounterEntityType,
    plan: ChangePlanner
  ): Promise<{ outcome: DeviceCounterSyncOutcome; state: DeviceCounterEntity; change: DeviceCounterStateChange }> {
    if (!cashRegisterId) throw new Error('Cash register id is required.');
    if (!entityType) throw new Error('Counter entity type is required.');
    const documentId = this.documentIdOf(cashRegisterId, entityType);

    // A counter is a read-modify-write, and the POS fires them back to back
    // (increment → reset → increment for one document number). Serialising per
    // document makes each attempt observe the previous one's local write, so
    // concurrent callers cannot all read the same pre-state and settle the
    // document on a superseded value.
    return this.serialize(documentId, () => this.firestoreService.withLock(`deviceCounter:${documentId}`, async () => {
      await this.firestoreService.waitForAuthUser();

      const ref = this.firestoreService.doc<DeviceCounterDocument>(`${this.firestoreCollection}/${documentId}`);
      const current = await this.readCurrentAsync(cashRegisterId, entityType);
      const change = plan(current, documentId);
      const occurredAt = toValidDate(change.occurredAt) ?? new Date();

      const creating = !current;
      const { data, options } = this.buildWrite(change, current, documentId, occurredAt, creating);
      const acknowledged = this.firestoreService.queueSetDoc(ref, data, options);
      return { outcome: { status: 'applied', created: creating, acknowledged } as DeviceCounterSyncOutcome, state: change.state, change };
    }));
  }

  /** The register's own document, or `undefined` when it does not exist yet. */
  private async readCurrentAsync(cashRegisterId: string, entityType: DeviceCounterEntityType): Promise<DeviceCounterDocument | undefined> {
    const stored = await this.readPathAsync<DeviceCounterDocument>(`${this.firestoreCollection}/${this.documentIdOf(cashRegisterId, entityType)}`);
    return stored ? this.normalizeDates(stored) : undefined;
  }

  /**
   * A document as it is really stored: the local cache first, then the server
   * when the cache has nothing.
   *
   * The second read is what stops a cold cache (a new browser profile, cleared
   * site data, a second browser on the same till) from looking like "this
   * counter does not exist" and restarting the series at 1 over a real value.
   * Offline with a cold cache the server cannot be asked, which stays the one
   * unavoidable gap.
   */
  private async readPathAsync<T>(path: string): Promise<T | undefined> {
    const ref = this.firestoreService.doc<T>(path);
    const cached = await this.firestoreService.getDocFromCacheAsync<T>(ref);
    if (cached) return cached;
    return (await this.firestoreService.getDocFromServerAsync<T>(ref)) ?? undefined;
  }

  /**
   * Run `work` after every earlier call for the same document. Only the
   * read-decide-queue section is serialised; the server acknowledgement is
   * never awaited here, so a slow or offline backend cannot stall the chain.
   */
  private serialize<T>(documentId: string, work: () => Promise<T>): Promise<T> {
    const previous = this.writeChains.get(documentId) ?? Promise.resolve();
    const next = previous.then(work, work);
    // Keep the chain alive after a rejection, and drop it once it is idle.
    const settled = next.then(() => undefined, () => undefined);
    this.writeChains.set(documentId, settled);
    settled.then(() => {
      if (this.writeChains.get(documentId) === settled) this.writeChains.delete(documentId);
    });
    return next;
  }

  private buildWrite(
    change: DeviceCounterStateChange,
    current: DeviceCounterDocument | undefined,
    documentId: string,
    occurredAt: Date,
    creating: boolean
  ): { data: Record<string, any>; options: SetOptions } {
    const data: Record<string, any> = this.toDocumentFields(change.state, current);
    const mergeFields: string[] = Object.keys(data);

    const now = new Date();
    const updatedBy = this.getSanitizedUserProfile();
    if (creating || !current?.metadata?.organizationInfo?.id) {
      const metadata = this.buildMetadata(documentId, current?.metadata);
      metadata.updatedDate = now;
      metadata.updatedBy = updatedBy;
      data['metadata'] = toFirestorePlainObject(metadata);
      mergeFields.push('metadata');
    } else {
      data['metadata'] = { updatedDate: now, updatedBy };
      mergeFields.push('metadata.updatedDate', 'metadata.updatedBy');
    }

    data['revision'] = {
      changedAt: occurredAt,
      lastOperation: change.operation,
      source: change.source,
      correlationId: change.correlationId
    };
    mergeFields.push('revision.changedAt', 'revision.lastOperation', 'revision.source', 'revision.correlationId');

    return { data, options: { mergeFields } };
  }

  //#endregion

  //#region Document shaping

  /**
   * The persisted shape of a counter: the counter fields as plain data with
   * real `Date`s. A `createdAt` already on the stored document
   * always wins, so a later write cannot move the counter's creation date.
   */
  private toDocumentFields(state: DeviceCounterEntity, current: DeviceCounterDocument | undefined): Record<string, any> {
    const plain: Record<string, any> = toFirestorePlainObject(state) ?? {};
    for (const field of [...DESKTOP_TRANSPORT_FIELDS, ...MANAGED_FIELDS]) {
      delete plain[field];
    }
    const normalized = this.normalizeDates(plain);
    const fields: Record<string, any> = {};
    for (const field of COUNTER_FIELDS) {
      if (normalized[field] !== undefined) fields[field] = normalized[field];
    }
    const storedCreatedAt = toValidDate(current?.createdAt);
    if (storedCreatedAt) fields['createdAt'] = storedCreatedAt;
    return fields;
  }

  private normalizeDates<T extends DeviceCounterDocument | Record<string, any>>(document: T): T {
    const record = document as any;
    record.lastResetAt = toValidDate(record.lastResetAt) ?? record.lastResetAt;
    record.createdAt = toValidDate(record.createdAt) ?? record.createdAt;
    return document;
  }

  private buildMetadata(documentId: string, existing: Metadata | undefined): Metadata {
    const holder: { metadata?: Metadata } = {
      metadata: { ...(existing ?? {}), localId: documentId } as Metadata
    };
    this.ensureValidMetadata(holder as DeviceCounterEntity);
    return holder.metadata!;
  }

  //#endregion
}
